// State ↔ Manifest converters.
//
// `manifestFromState` extracts the small per-entity descriptor (source,
// palette ref, seed) — everything else comes from the lib's current
// defaults. Variant info isn't carried for URL sources because the
// variant is baked into the source PNG itself for cover images, and N/A
// for user-uploaded ones. For synthetic sources, the variant + seed live
// in the source descriptor.
//
// `manifestToState` resolves the symbolic palette ref against the live
// CSS tokens and fills the rest of the state from `DEFAULT_STATE` /
// `DEFAULT_PARAMS`. This is what makes lib changes propagate.

import { CATEGORY_PALETTE_MAP } from '../presets/category-palettes';
import { DEFAULT_STATE } from '../presets/defaults';
import { resolveOutcomePalette } from '../presets/outcome-palettes';
import { DEFAULT_PALETTE_NAME, resolveBrandPalette } from '../presets/palettes';
import type {
  MosaicConfig,
  PatternParams,
  PatternRendererKind,
  PatternState,
  SampledImage,
} from '../types';
import type { ManifestSource, PaletteRef, PatternManifest } from './types';
import { MANIFEST_VERSION } from './types';

/**
 * Workspace-only debug overrides for `manifestToState`. When a field is set,
 * it replaces the corresponding value from `DEFAULT_STATE` / `DEFAULT_PARAMS`
 * for this single conversion. Production callers MUST leave both fields
 * undefined — that preserves the propagation contract (lib changes flow to
 * every existing entity). Used by /workspace/pattern to make MarketRowCards
 * react live to tuner knobs.
 */
export interface ManifestToStateOverrides {
  readonly params?: PatternParams;
  readonly mosaic?: MosaicConfig;
}

/**
 * Resolve a `PaletteRef` to a live hex `Palette` against the current
 * lib defaults + `:root` CSS variables. Browser-only at runtime —
 * falls back to lib hex defaults if `getComputedStyle` is unavailable.
 *
 * For `category` refs, looks up the brand preset via CATEGORY_PALETTE_MAP
 * and falls back to the default brand preset for unknown categories.
 */
export function resolvePaletteRef(ref: PaletteRef): readonly string[] {
  if (ref.kind === 'preset') return resolveBrandPalette(ref.name);
  if (ref.kind === 'outcome-hue') return resolveOutcomePalette(ref.hue);
  // ref.kind === 'category'
  const presetName = CATEGORY_PALETTE_MAP[ref.category] ?? DEFAULT_PALETTE_NAME;
  return resolveBrandPalette(presetName);
}

/**
 * Build a manifest from a workspace state + the symbolic palette
 * reference the user picked + the source descriptor (URL of the source
 * PNG, or a synthetic variant+seed).
 *
 * Per-asset overrides (cellSize, mBrightness, etc.) are NOT captured by
 * design — they would defeat the propagation contract.
 */
export function manifestFromState(
  state: PatternState,
  source: ManifestSource,
  paletteRef: PaletteRef,
): PatternManifest {
  return {
    version: MANIFEST_VERSION,
    source,
    palette: paletteRef,
    seed: state.seed,
  };
}

/** Avatars use preset palettes and keep the Floyd–Steinberg mosaic renderer. */
export function rendererForPalette(ref: PaletteRef): PatternRendererKind {
  return ref.kind === 'preset' ? 'mosaic' : 'halftone';
}

/**
 * Hydrate a manifest into a workspace state. Used internally by
 * `renderManifest`; consumers usually don't need to call this directly.
 */
export function manifestToState(
  manifest: PatternManifest,
  sampled: SampledImage | null = null,
  overrides: ManifestToStateOverrides = {},
): PatternState {
  const palette = resolvePaletteRef(manifest.palette);
  const bg = palette[0] ?? DEFAULT_STATE.bg;
  const renderer = rendererForPalette(manifest.palette);
  return {
    ...DEFAULT_STATE,
    renderer,
    palette,
    bg,
    seed: manifest.seed,
    image: { loaded: sampled !== null, shape: 'full', sampled },
    ...(overrides.params !== undefined ? { params: overrides.params } : {}),
    ...(overrides.mosaic !== undefined ? { mosaic: overrides.mosaic } : {}),
  };
}
